<script setup>
    // Vue
    import { ref, defineEmits, defineExpose, inject } from 'vue';

    // Component
    import BaseInput from '@/components/BaseInputComponent.vue';
    import BaseSelect from '@/components/BaseSelectComponent.vue';

    // Store
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useLanguageStore } from '@/stores/common/languageStore';
    import { useLoginStore } from '@/stores/login/loginStore';
    import { useUserDataUpdateStore } from '@/stores/modal/userDataUpdateStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Utils
    import Utils from '../../commonProject/helpers/utils.js';

    // Else
    import i18n from '@/common/i18n';

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    // 親画面から取得したAPIのドメイン部を管理する。
    const API_URL = ref('');

    // 各種セレクトの値はどのようにして取得するか
    const GENDER = ref([]);

    // 各種セレクトの値はどのようにして取得するか
    const LANG = ref([]);

    // ----------------------------
    // ユーザー情報更新
    // ----------------------------
    const userDataUpdateStore = useUserDataUpdateStore();
    const commonStore = useCommonStore();
    const languageStore = useLanguageStore();
    const loginStore = useLoginStore();

    const YEAR = ref([]);
    const MONTH = ref([]);
    const DAY = ref([]);

    const yearValue = ref('');
    const monthValue = ref('');
    const dayValue = ref('');

    // ユーザーの「登録契機」に応じた編集モードの判定。
    const userRegistMethodMode = ref(false);

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    /**
     * ユーザ情報取得処理
     * @param queryParam
     * @returns true:正常 false:異常
     */
    async function getUserInfo() {
        const response = await userDataUpdateStore.getUserAction(API_URL.value, null);
        if (!response.success) {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return false;
        }
        return true;
    }

    /**
     * ユーザー情報詳細取得処理
     * @param queryParam
     */
    async function getUserDetail() {
        const response = await userDataUpdateStore.getUserDetailAction(API_URL.value, null);
        if (response.success) {
            const birthDayList = userDataUpdateStore.userDetail.Birthday.split('/');
            yearValue.value = birthDayList[0];
            monthValue.value = birthDayList[1];
            dayValue.value = birthDayList[2];
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    // ----------------------------
    // 親画面で実装必須関数
    // ----------------------------
    const emit = defineEmits(['userDataUpdateModal:cancel', 'userDataUpdateModal:update']);
    function cancel() {
        // 親画面のキャンセル処理を呼び出す
        emit('userDataUpdateModal:cancel');
    }

    /**
     * ユーザー情報更新処理
     * @param queryParam
     */
    async function update() {
        // バリデーションチェック
        if (isValidationError()) {
            return;
        }

        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();

        // ユーザー詳細設定APIをリクエストボディを生成する。
        let requestBody = {};

        // ユーザーの「登録契機」が自己登録と自己登録(オペレータ)の場合は画面の氏名の入力欄の情報を取得する。
        if (userRegistMethodMode.value) {
            requestBody['NameKjLs'] = userDataUpdateStore.userDetail.NameKjLs;
            requestBody['NameKjFs'] = userDataUpdateStore.userDetail.NameKjFs;
            // 氏名（カナ）の関連項目は任意項目だが、もともと入力されていたものを空に変更する可能性も考慮して、更新時は画面上の入力情報を設定する。
            requestBody['NameKnLs'] = userDataUpdateStore.userDetail.NameKnLs;
            requestBody['NameKnFs'] = userDataUpdateStore.userDetail.NameKnFs;
        }

        requestBody['MailAddress'] = userDataUpdateStore.userDetail.MailAddress;
        requestBody['Birthday'] = yearValue.value + '/' + monthValue.value + '/' + dayValue.value;
        requestBody['Gender'] = userDataUpdateStore.userDetail.Gender;
        requestBody['Lang'] = userDataUpdateStore.userDetail.Lang;
        requestBody['PhoneNumber'] = userDataUpdateStore.userDetail.PhoneNumber;
        requestBody['UserKeyNo'] = userDataUpdateStore.userDetail.UserKeyNo;

        // ユーザー詳細設定APIをコールする。
        const response = await userDataUpdateStore.updateUserAction(API_URL.value, requestBody);
        if (response.success) {
            // ログイン中ユーザー情報を格納するStore情報を初期化する。
            // 更新後のユーザー情報をAPIから取得し、最新のユーザー情報を格納しなおす。
            loginStore.userId = '';
            loginStore.nameKjLs = '';
            loginStore.nameKjFs = '';
            loginStore.vendorType = '';
            loginStore.userKeyNoSupport = '';
            loginStore.userKeyNoNm = '';
            loginStore.phoneNumber = '';
            loginStore.mailAddress = '';

            // ユーザー情報取得APIをコールする。クエリパラメーターの条件は設定しない。
            const response2 = await loginStore.getLoginUserInfoAction(API_URL.value, null);
            if (response2.success) {
                // 親画面の更新後処理を呼び出す
                emit('userDataUpdateModal:update');
            } else {
                messageDialog('error', response2.errorMessage);
                if (response2.redirect) {
                    location.href = response2.redirectUrl;
                }
                return;
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
        }
    }

    /**
     * 親画面からユーザー情報更新モーダルを開くイベントが実行された場合の処理。
     * @param {String} trg HTMLファイルタグが生成されている座標を取得する。
     * @returns なし
     */
    async function openModal(trg) {
        // Storeを初期化する。
        userDataUpdateStore.reset();

        // 初期化。
        YEAR.value = [];
        MONTH.value = [];
        DAY.value = [];
        GENDER.value = [];
        LANG.value = [];

        // 生年月日の年のプルダウンで選択されている座標を初期化。
        yearValue.value = '';

        // 生年月日の月のプルダウンで選択されている座標を初期化。
        monthValue.value = '';

        // 生年月日の日のプルダウンで選択されている座標を初期化。
        dayValue.value = '';

        // 現在日時取得
        const date = new Date();

        // 現在の年から100年前を取得
        const dateBase = date.getFullYear();
        for (let i = dateBase; i >= 1900; i--) {
            YEAR.value.push({ pk: i, name: i });
        }

        for (let i = 1; i < 13; i++) {
            MONTH.value.push({ pk: setZeroToString(i), name: i });
        }

        for (let i = 1; i < 32; i++) {
            DAY.value.push({ pk: setZeroToString(i), name: i });
        }

        for (const data in commonStore.getDictionary('Gender')) {
            GENDER.value.push({ pk: data, name: commonStore.getConvertDictionary('Gender', data, languageStore.getLanguageValue) });
        }

        for (const data in commonStore.getDictionary('Lang')) {
            LANG.value.push({ pk: data, name: commonStore.getConvertDictionary('Lang', data, languageStore.getLanguageValue) });
        }

        API_URL.value = trg;

        // ユーザー情報取得
        const result = await getUserInfo();

        if (!result) {
            return;
        }

        // ユーザーの「登録契機」（"1"：管理者登録、"2"：自己登録、"3"：管理者登録(オペレータ)、"4"：自己登録(オペレータ)、"5"：タフネス連携）に応じて編集モードを制御する。
        // が自己登録と自己登録(オペレータ)の場合は、画面の氏名の入力欄を編集可能にする。
        if (userDataUpdateStore.userModel.RegistMethod === '2' || userDataUpdateStore.userModel.RegistMethod === '4') {
            userRegistMethodMode.value = true;
        }

        await getUserDetail();

        function setZeroToString(num) {
            return num < 10 ? String('0' + num) : String(num);
        }

        // モーダルのHTML要素を取得する。
        const elm1 = document.querySelector('#modalUserInfoUpdate .modalContents');

        // モーダルの縦スクロールを先頭に戻す。
        elm1.scrollTop = 0;
    }

    /**
     * バリデーションチェック
     */
    function isValidationError() {
        // エラーメッセージを初期化する。
        let message = '';

        if (userRegistMethodMode.value) {
            // 氏名(漢字)の姓の入力チェック。
            if (Utils.isNullEmpty(userDataUpdateStore.userDetail.NameKjLs)) {
                message += i18n.global.t('userDataUpdate.message.message1') + '\n';
            } else {
                if (Utils.checkLengthOfShortString(userDataUpdateStore.userDetail.NameKjLs)) {
                    message += i18n.global.t('userDataUpdate.message.message2') + '\n';
                }
            }

            // 氏名(漢字)の名の入力チェック。
            if (Utils.isNullEmpty(userDataUpdateStore.userDetail.NameKjFs)) {
                message += i18n.global.t('userDataUpdate.message.message3') + '\n';
            } else {
                if (Utils.checkLengthOfShortString(userDataUpdateStore.userDetail.NameKjFs)) {
                    message += i18n.global.t('userDataUpdate.message.message4') + '\n';
                }
            }

            // 任意項目である、氏名(カナ)のセイの入力チェック。
            if (!Utils.isNullEmpty(userDataUpdateStore.userDetail.NameKnLs)) {
                if (Utils.checkLengthOfString(userDataUpdateStore.userDetail.NameKnLs)) {
                    message += i18n.global.t('userDataUpdate.message.message6') + '\n';
                }
                if (!Utils.isMatchPatternKana(userDataUpdateStore.userDetail.NameKnLs)) {
                    message += i18n.global.t('userDataUpdate.message.message7') + '\n';
                }
            }

            // 任意項目である、氏名(カナ)のメイの入力チェック。
            if (!Utils.isNullEmpty(userDataUpdateStore.userDetail.NameKnFs)) {
                if (Utils.checkLengthOfString(userDataUpdateStore.userDetail.NameKnFs)) {
                    message += i18n.global.t('userDataUpdate.message.message9') + '\n';
                }
                if (!Utils.isMatchPatternKana(userDataUpdateStore.userDetail.NameKnFs)) {
                    message += i18n.global.t('userDataUpdate.message.message10') + '\n';
                }
            }
        }

        // メールアドレスの入力チェック。
        if (Utils.isNullEmpty(userDataUpdateStore.userDetail.MailAddress)) {
            message += i18n.global.t('userDataUpdate.message.message11') + '\n';
        } else {
            if (Utils.checkLengthOfMailAddress(userDataUpdateStore.userDetail.MailAddress)) {
                message += i18n.global.t('userDataUpdate.message.message12') + '\n';
            }
            if (!Utils.isMatchPatternMailAddress(userDataUpdateStore.userDetail.MailAddress)) {
                message += i18n.global.t('userDataUpdate.message.message13') + '\n';
            }
        }

        // 生年月日の年の選択チェック。
        if (Utils.isNullEmpty(yearValue.value)) {
            message += i18n.global.t('userDataUpdate.message.message14') + '\n';
        }

        // 生年月日の月の選択チェック。
        if (Utils.isNullEmpty(monthValue.value)) {
            message += i18n.global.t('userDataUpdate.message.message15') + '\n';
        }

        // 生年月日の日の選択チェック。
        if (Utils.isNullEmpty(dayValue.value)) {
            message += i18n.global.t('userDataUpdate.message.message16') + '\n';
        }

        if (!Utils.isNullEmpty(yearValue.value) && !Utils.isNullEmpty(monthValue.value) && !Utils.isNullEmpty(dayValue.value)) {
            // 日付の妥当性をチェック
            const selectDate = new Date(Number(yearValue.value), Number(monthValue.value) - 1, Number(dayValue.value));
            const yearDate = selectDate.getFullYear();
            const monthDate = selectDate.getMonth() + 1;
            const dayDate = selectDate.getDate();
 
            const selectDate1 = yearDate + '/' + String(monthDate).padStart(2, '0') + '/' + String(dayDate).padStart(2, '0');
            const selectDate2 = yearValue.value + '/' + monthValue.value + '/' + dayValue.value;
            if (selectDate1 !== selectDate2) {
                message += i18n.global.t('userDataUpdate.message.message25') + '\n';
            }
            // 生年月日が未来日かチェック
            let todaySet = new Date();
            let YYYY = todaySet.getFullYear();
            let MM = ('00' + (todaySet.getMonth() + 1)).slice(-2);
            let DD = ('00' + todaySet.getDate()).slice(-2);
            let today = YYYY + '/' + MM + '/' + DD;
            if (today < selectDate1) {
                message += i18n.global.t('userDataUpdate.message.message25') + '\n';
            }

        }

        // 性別の選択チェック。
        if (Utils.isNullEmpty(userDataUpdateStore.userDetail.Gender)) {
            message += i18n.global.t('userDataUpdate.message.message17') + '\n';
        }

        // 言語の選択チェック。
        if (Utils.isNullEmpty(userDataUpdateStore.userDetail.Lang)) {
            message += i18n.global.t('userDataUpdate.message.message18') + '\n';
        }

        // 電話番号の入力チェック。
        if (Utils.isNullEmpty(userDataUpdateStore.userDetail.PhoneNumber)) {
            message += i18n.global.t('userDataUpdate.message.message19') + '\n';
        } else {
            if (Utils.checkLengthOfTelNumber(userDataUpdateStore.userDetail.PhoneNumber)) {
                message += i18n.global.t('userDataUpdate.message.message20') + '\n';
            }
            if (!Utils.isMatchPatternTelNumber(userDataUpdateStore.userDetail.PhoneNumber)) {
                message += i18n.global.t('userDataUpdate.message.message21') + '\n';
            }
        }

        // ユーザー独自のユーザーキー番号が設定されている場合のみ、ユーザーキー番号のチェックを行う。
        if (userDataUpdateStore.userModel.UserKeyNoSupport) {
            if (Utils.isNullEmpty(userDataUpdateStore.userDetail.UserKeyNo)) {
                message += userDataUpdateStore.userModel.UserKeyNoNm + i18n.global.t('userDataUpdate.message.message22') + '\n';
            } else {
                if (Utils.checkLengthOfShortString(userDataUpdateStore.userDetail.UserKeyNo)) {
                    message += userDataUpdateStore.userModel.UserKeyNoNm + i18n.global.t('userDataUpdate.message.message23') + '\n';
                }
                if (!Utils.isMatchPatternAlphabetNumber(userDataUpdateStore.userDetail.UserKeyNo)) {
                    message += userDataUpdateStore.userModel.UserKeyNoNm + i18n.global.t('userDataUpdate.message.message24') + '\n';
                }
            }
        }

        // エラーメッセージが存在する場合は、以降の処理に進ませないので、バリデーションチェック判定を返却する。
        if (!Utils.isNullEmpty(message)) {
            // エラーメッセージが存在する場合は、画面に表示する。
            messageDialog('error', message);
            return true;
        }

        return false;
    }

    defineExpose({
        openModal,
    });
</script>

<template>
    <article id="modalUserInfoUpdate" class="modal" :style="{ display: 'block' }">
        <h3>{{ $t('userDataUpdate.parts.modalTitle1') }}</h3>
        <section class="modalContents double">
            <section class="form">
                <dl>
                    <!--氏名(漢字)-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('userDataUpdate.parts.dl1') }}</label>
                            </li>
                            <li class="must">
                                <span>{{ $t('common.parts.must') }}</span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput" style="align-items: flex-start;">
                            <li>
                                <template v-if="userRegistMethodMode">
                                    <BaseInput v-model:input="userDataUpdateStore.userDetail.NameKjLs" type="text" :placeholder="$t('userDataUpdate.parts.placeholder1')" />
                                </template>
                                <template v-else>
                                    <span class="disabled">{{ userDataUpdateStore.userDetail.NameKjLs }}</span>
                                </template>
                            </li>
                            <li>
                                <template v-if="userRegistMethodMode">
                                    <BaseInput v-model:input="userDataUpdateStore.userDetail.NameKjFs" type="text" :placeholder="$t('userDataUpdate.parts.placeholder2')" />
                                </template>
                                <template v-else>
                                    <span class="disabled">{{ userDataUpdateStore.userDetail.NameKjFs }}</span>
                                </template>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--氏名(カナ)-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('userDataUpdate.parts.dl2') }}</label>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput" style="align-items: flex-start;">
                            <li>
                                <template v-if="userRegistMethodMode">
                                    <BaseInput v-model:input="userDataUpdateStore.userDetail.NameKnLs" type="text" :placeholder="$t('userDataUpdate.parts.placeholder3')" />
                                </template>
                                <template v-else>
                                    <span class="disabled">{{ userDataUpdateStore.userDetail.NameKnLs }}</span>
                                </template>
                            </li>
                            <li>
                                <template v-if="userRegistMethodMode">
                                    <BaseInput v-model:input="userDataUpdateStore.userDetail.NameKnFs" type="text" :placeholder="$t('userDataUpdate.parts.placeholder4')" />
                                </template>
                                <template v-else>
                                    <span class="disabled">{{ userDataUpdateStore.userDetail.NameKnFs }}</span>
                                </template>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--メールアドレス-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label style="width: auto">{{ $t('userDataUpdate.parts.dl3') }}</label>
                            </li>
                            <li class="must">
                                <span>{{ $t('common.parts.must') }}</span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput spKeepFlex">
                            <li>
                                <BaseInput v-model:input="userDataUpdateStore.userDetail.MailAddress" type="email" class="wideField" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--生年月日-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label style="width: auto">{{ $t('userDataUpdate.parts.dl4') }}</label>
                            </li>
                            <li class="must">
                                <span>{{ $t('common.parts.must') }}</span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput birthday">
                            <li>
                                <BaseSelect v-model="yearValue" :options="YEAR" required />
                            </li>
                            <li>
                                <!--年-->
                                <span>{{ $t('userDataUpdate.parts.dl4Label1') }}</span>
                            </li>
                            <li>
                                <BaseSelect v-model="monthValue" :options="MONTH" required />
                            </li>
                            <li>
                                <!--月-->
                                <span>{{ $t('userDataUpdate.parts.dl4Label2') }}</span>
                            </li>
                            <li>
                                <BaseSelect v-model="dayValue" :options="DAY" required />
                            </li>
                            <li>
                                <!--日-->
                                <span>{{ $t('userDataUpdate.parts.dl4Label3') }}</span>
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--性別-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('userDataUpdate.parts.dl5') }}</label>
                            </li>
                            <li class="must">
                                <span>{{ $t('common.parts.must') }}</span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <BaseSelect v-model="userDataUpdateStore.userDetail.Gender" :options="GENDER" required />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--言語-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('userDataUpdate.parts.dl6') }}</label>
                            </li>
                            <li class="must">
                                <span>{{ $t('common.parts.must') }}</span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput spKeepFlex">
                            <li>
                                <BaseSelect v-model="userDataUpdateStore.userDetail.Lang" :options="LANG" required />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <dl>
                    <!--電話番号-->
                    <dt>
                        <ul class="formLabel">
                            <li>
                                <label>{{ $t('userDataUpdate.parts.dl7') }}</label>
                            </li>
                            <li class="must">
                                <span>{{ $t('common.parts.must') }}</span>
                            </li>
                        </ul>
                    </dt>
                    <dd>
                        <ul class="formInput">
                            <li>
                                <BaseInput v-model:input="userDataUpdateStore.userDetail.PhoneNumber" type="text" />
                            </li>
                        </ul>
                    </dd>
                </dl>
                <template v-if="userDataUpdateStore.userModel.UserKeyNoSupport">
                    <dl id="userKeyNumberName">
                        <!--ユーザーキー情報-->
                        <dt>
                            <ul class="formLabel">
                                <li>
                                    <label>{{ userDataUpdateStore.userModel.UserKeyNoNm }}</label>
                                </li>
                                <li class="must">
                                    <span>{{ $t('common.parts.must') }}</span>
                                </li>
                            </ul>
                        </dt>
                        <dd>
                            <ul class="formInput">
                                <li>
                                    <BaseInput v-model:input="userDataUpdateStore.userDetail.UserKeyNo" type="tel" />
                                </li>
                            </ul>
                        </dd>
                    </dl>
                </template>
            </section>
        </section>
        <section class="modalFooter">
            <ul class="btnArea center">
                <li>
                    <!--キャンセル-->
                    <button type="button" class="btnNormal" @click="cancel">{{ $t('common.parts.cancel') }}</button>
                </li>
                <li>
                    <!--登録-->
                    <button type="button" class="btnPrimary" @click="update" :disabled="disabledStore.isDisabled">{{ $t('common.parts.entry') }}</button>
                </li>
            </ul>
        </section>
    </article>
</template>

<style scoped>
    #overlay .modal .modalContents > .form > dl > dt {
        width: 265px;
        height: auto;
    }
    #overlay .modal .modalContents > .form > dl > dd {
        width: calc(100% - 265px);
    }
    @media screen and (max-width: 1280px) {
        #overlay .modal .modalContents > .form > dl > dt {
            width: 100%;
            margin-bottom: 2px;
        }
        #overlay .modal .modalContents > .form > dl > dd {
            width: 100%;
        }
    }
    #overlay .modal .modalContents > .form > dl > dt > ul.formLabel > li {
        width: 160px;
    }
    #overlay .modal .modalContents > .innerTable table.table thead tr th:nth-child(1),
    #overlay .modal .modalContents > .innerTable table.table tbody tr td:nth-child(1) {
        width: 150px;
        padding-left: 10px;
    }
    #overlay .modal .modalContents > .innerTable table.table thead tr th:nth-child(2),
    #overlay .modal .modalContents > .innerTable table.table tbody tr td:nth-child(2) {
        width: 150px;
    }
    #overlay .modal .modalContents > .form > dl > dd > ul.formInput > li span.disabled {
        width: 100%;
        height: auto;
        position: relative;
        display: block;
        line-height: 20px;
        padding-top: 10px;
        padding-bottom: 10px;
    }
</style>
